/**
 * An on/off switch — the iOS-style track and thumb the sidebar's Test Mode
 * toggle drew inline, extracted the first time a second screen needed three of
 * them.
 *
 * A real `<button role="switch">` rather than a styled checkbox, so it is
 * focusable, operable from the keyboard, and announced as a switch with its
 * state by a screen reader.
 */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Visible label. Omit only when an `aria-label` is supplied instead. */
  label?: string;
  /** One line under the label saying what turning this on actually does. */
  description?: string;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
  ...rest
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={rest['aria-label'] ?? (label ? undefined : 'Comută')}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'flex w-full items-start gap-3 rounded-md px-1 py-2 text-left transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100/60',
        className,
      )}
    >
      {(label || description) && (
        <span className="min-w-0 flex-1">
          {label && <span className="block text-sm font-medium text-gray-900">{label}</span>}
          {description && <span className="mt-0.5 block text-xs text-gray-500">{description}</span>}
        </span>
      )}
      {/* Track */}
      <span
        aria-hidden
        className={cx(
          'relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-brand-600' : 'bg-muted',
        )}
      >
        {/* Thumb */}
        <span
          className={cx(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  );
}
